/**
 * Animation utility functions for entity visual effects.
 * All functions are pure — no side effects or state.
 */

/** Oscillating value between min and max at given frequency (Hz). */
export function pulse(
  time: number,
  frequency: number,
  min: number,
  max: number,
): number {
  const t = (Math.sin(time * frequency * Math.PI * 2) + 1) / 2;
  return min + t * (max - min);
}

/** Smooth sine wave 0..1 at given frequency (Hz). */
export function breathe(time: number, frequency: number): number {
  return (Math.sin(time * frequency * Math.PI * 2) + 1) / 2;
}

/** Returns true on random off-frames with given probability (0..1). */
export function flicker(time: number, probability: number): boolean {
  // Use time to seed a pseudo-random check per frame
  const hash = Math.sin(time * 9999.137) * 10000;
  return hash - Math.floor(hash) < probability;
}

// ── Easing (the ART §6.8 motion vocabulary) ──────────────────────────────────
// Pure unit-interval easings: f(0) = 0, f(1) = 1. Drive transition VFX by
// feeding a normalized 0..1 progress. snap/settle seeded the set in BL-49 (the
// cloak/reveal phase-in was the first consumer); BL-36 completes it with
// glide/decay/anticipate and wires the entity reactions (hit/spawn/death/recoil).
// `snap` overshoots >1 and `anticipate` dips <0 — both intentional, so neither
// is monotonic; the rest stay inside [0,1]. ART §6.8's table mirrors these.

/** Overshoot constant for {@link snap} (standard ease-out-back tension). */
const SNAP_BACK = 1.70158;

/**
 * Ease-out-back: accelerates out, **overshoots past 1**, then settles back to 1.
 * The overshoot is what reads as a physical "snap" / punch on materialisation.
 * f(0) = 0, f(1) = 1, peaks ~1.05 around t≈0.8.
 */
export function snap(t: number): number {
  const c1 = SNAP_BACK;
  const c3 = c1 + 1;
  const x = t - 1;
  return 1 + c3 * x * x * x + c1 * x * x;
}

/**
 * Ease-out-cubic: fast departure, gentle deceleration into rest — no overshoot.
 * The "settle" half of a snap→settle pair. Monotonic on [0,1]; f(0)=0, f(1)=1.
 */
export function settle(t: number): number {
  const x = 1 - t;
  return 1 - x * x * x;
}

/**
 * Ease-in-out-quad: a symmetric S-curve for smooth value lerps / camera glides
 * and any "continuous, no-snap" transition. Monotonic on [0,1], no overshoot;
 * f(0)=0, f(0.5)=0.5, f(1)=1.
 */
export function glide(t: number): number {
  return t < 0.5 ? 2 * t * t : 1 - ((-2 * t + 2) * (-2 * t + 2)) / 2;
}

/**
 * Exponential falloff (ease-out-expo): a fast departure then a long flat tail —
 * the natural shape for a flash / trail dissipating, where the *rate* of change
 * decays. Monotonic on [0,1]; f(0)=0, f(1)=1. Drive a fade as `1 - decay(t)`
 * (bright → dark, quick then slow).
 */
export function decay(t: number): number {
  return t >= 1 ? 1 : 1 - Math.pow(2, -10 * t);
}

/**
 * Ease-in-back: a **wind-up that dips below 0** before driving to 1 — the
 * anticipation before a strike (the mirror of {@link snap}). f(0)=0, f(1)=1;
 * undershoots <0 around t≈0.2. Not monotonic by design (the dip *is* the feel).
 */
export function anticipate(t: number): number {
  const c1 = SNAP_BACK;
  const c3 = c1 + 1;
  return c3 * t * t * t - c1 * t * t;
}
